import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import './DetailsList.scss';

import { 
    Metadata,
    Submission, 
    SubmissionRequestSummary,
    PortalTextCommon,
    PortalTextCommonImport,
    PortalTextSubmissionRequestSummary,
} from '../../Models/Api/strongbox.financialportal';
import { PortalSubmissionRequestFromSubmissionRequestSummary } from '../../Models/PortalCreateSubmissionRequestOptions';
import { knownAccountingGroupIds } from '../../Models/AccountingCollectionGroup';

import { ApplicationState } from '../../Store';
import { GetUserById, LoadingUsers } from '../../Store/User';
import { GetPortalText } from '../../Store/PortalAssets';
import {
    GetDisableAdditionalDocUpload,
    GetDisableProvideuserCopy,
    GetDisableAnonymizeResults,
    GetDisableBasisOfAccounting,
    GetDefaultSubmissionOptions,
} from '../../Store/AppSettings';

import { ConnectionSourceText } from '../../Utils/Constants';
import { GetBasisOfAccountingDescription, GetCollectionRangeSummary } from '../../Utils/GlobalMessageUtils';
import { FormatDate, formatStringMDYFullMonth } from '../../Utils/DateUtils';

import {
    PortalCreateSubmissionRequestOptions
} from '../../Models/PortalCreateSubmissionRequestOptions';

type InjectedReduxState = {
    loadingUsers: boolean;
    userDisplayName?: string;
    fileUploadDisabled: boolean;
    provideUserCopyDisabled: boolean;
    anonymizeResultsDisabled: boolean;
    basisOfAccountingDisabled: boolean;
    defaultSubmissionOptions: PortalCreateSubmissionRequestOptions,
    commonText?: PortalTextCommon,
    srsText?: PortalTextSubmissionRequestSummary,
};

type InjectedActionCreators = {};

type RequestSummaryProps = {
    /** Added to the <dl> this renders (not used when `inList` is set). */
    className?: string;
    /** The caller already renders the surrounding <dl> (e.g. the overview's details list), so render only its rows. */
    inList?: boolean;
    requestSummary?: SubmissionRequestSummary;
    loadedUserDisplayName?: string;
    submission?: Submission;
    submissionMetadata?: Metadata[];
    showTypicalImportTime?: boolean;
};

type Props = RequestSummaryProps & InjectedReduxState & InjectedReduxState;

export const RequestSummaryComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        className,
        inList,
        loadingUsers,
        requestSummary,
        submission,
        userDisplayName,
        defaultSubmissionOptions,
        commonText,
        srsText,
    } = props;

    const loadingUsersContent = (): React.ReactElement => {
        return (
            <div className={'page-details-sidebar__loading'}>{commonText?.loading || ''}</div>
        )
    }

    const collectingPeriodText = (): string | undefined => {
        let result = undefined;

        if (!requestSummary) {
            result = GetCollectionRangeSummary(defaultSubmissionOptions, commonText);
        } else if (
            (requestSummary.mostRecentPeriodMonth !== undefined) &&
            (requestSummary.mostRecentPeriodYear !== undefined)
        ) {
            const rs = PortalSubmissionRequestFromSubmissionRequestSummary(requestSummary);
            result = GetCollectionRangeSummary(rs, commonText);
        }
        return result;
    }

    const basisOfAccounting = React.useMemo(() => {
        if (!!(requestSummary)) {
            return GetBasisOfAccountingDescription(PortalSubmissionRequestFromSubmissionRequestSummary(requestSummary), commonText);
        }
        return commonText?.basisOfAccountingNA || '';

        // requestSummary is the only dependency I want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestSummary]);

    const getAccountingDataLabel = (id: string): string => {
        const accountingGroup = defaultSubmissionOptions.accountingCollectionGroups.get(id);
        return !!accountingGroup ? (accountingGroup.name || '') : '';
    }

    const getDefaultAccountingGroupCollectionState = (id: string): boolean => {
        const accountingGroup = defaultSubmissionOptions.accountingCollectionGroups.get(id);
        return !!accountingGroup && accountingGroup.collect;
    }

    const requestSummaryText = React.useMemo(() => {
        const summary = requestSummary || defaultSubmissionOptions;

        const linkType = !requestSummary ? (commonText?.tooltipAnonymousDC || '') : 
            (requestSummary.hasShareableLink ? ConnectionSourceText.SecureUrlText : ConnectionSourceText.DirectConnectionText);
        const anonymize = summary.anonymizeCustomersAndVendors ? (commonText?.yes || '') : (commonText?.no || '');
        const allowUserUpload = summary.allowUserUpload ? (commonText?.yes || '') : (commonText?.no || '');
        const provideUserCopy = summary.provideUserCopy ? (commonText?.yes || '') : (commonText?.no || '');
        const collectFinancialStatements = 
            (!!requestSummary && (!!requestSummary.financialStatementsYearsToCollect && requestSummary.financialStatementsYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.financialStatements)) ? (commonText?.yes || '') : (commonText?.no || '');
        const collectTxns = 
            (!!requestSummary && (!!requestSummary.transactionsYearsToCollect && requestSummary.transactionsYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.transactions)) ? (commonText?.yes || '') : (commonText?.no || '');
        const collectReceivables = 
            (!!requestSummary && (!!requestSummary.receivablesYearsToCollect && requestSummary.receivablesYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.accountsReceivable)) ? (commonText?.yes || '') : (commonText?.no || '');
        const collectPayables = 
            (!!requestSummary && (!!requestSummary.payablesYearsToCollect && requestSummary.payablesYearsToCollect > 0)) ||
            (!requestSummary && getDefaultAccountingGroupCollectionState(knownAccountingGroupIds.accountsPayable)) ? (commonText?.yes || '') : (commonText?.no || '');

        return {
            linkType,
            anonymize,
            anonymizeYes: !!summary.anonymizeCustomersAndVendors,
            allowUserUpload,
            allowUserUploadYes: !!summary.allowUserUpload,
            provideUserCopy,
            provideUserCopyYes: !!summary.provideUserCopy,
            collectFinancialStatements,
            collectTxns,
            collectReceivables,
            collectPayables,
        };

        // This is the correct dependency list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestSummary, defaultSubmissionOptions, commonText])

    // One detail per row, in the order of the prototype's Collection details list. `tag` rows show the value as a
    // success tag when `affirmative` is true. That is decided from the underlying boolean, not by comparing the
    // displayed text with the localized "yes".
    const summaryRows: { prompt: string, value: string, tag?: boolean, affirmative?: boolean }[] = React.useMemo(() => {
        const collectedThrough = collectingPeriodText();

        return [
            {
                prompt: srsText?.originatingFrom || '',
                value: requestSummaryText.linkType,
            },
            {
                prompt: srsText?.collectedOn || '',
                value: !!submission ? FormatDate(new Date(submission.creationTime), formatStringMDYFullMonth) : '',
            },
            {
                prompt: srsText?.requestedByPortalUser || '',
                value: !!userDisplayName ? userDisplayName : (srsText?.userNotAssociatedWithSubmission || ''),
            },
            {
                prompt: srsText?.preferredBasisOfAccounting || '',
                value: basisOfAccounting,
            },
            {
                prompt: srsText?.collectionPeriod || '',
                value: collectedThrough || '',
            },
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.financialStatements),
                value: requestSummaryText.collectFinancialStatements,
            },
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.transactions),
                value: requestSummaryText.collectTxns,
            },
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.accountsReceivable),
                value: requestSummaryText.collectReceivables,
            },
            {
                prompt: getAccountingDataLabel(knownAccountingGroupIds.accountsPayable),
                value: requestSummaryText.collectPayables,
            },
            {
                prompt: srsText?.customersAndVendorsAnonymized || '',
                value: requestSummaryText.anonymize,
                tag: true,
                affirmative: requestSummaryText.anonymizeYes,
            },
            {
                prompt: srsText?.userMayUpload || '',
                value: requestSummaryText.allowUserUpload,
                tag: true,
                affirmative: requestSummaryText.allowUserUploadYes,
            },
            {
                prompt: srsText?.userMayDownloadCopy || '',
                value: requestSummaryText.provideUserCopy,
                tag: true,
                affirmative: requestSummaryText.provideUserCopyYes,
            },
        ];

        // These are the dependencies I really want
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commonText, srsText, requestSummary, submission, basisOfAccounting, userDisplayName, defaultSubmissionOptions]);

    const regularContent = (): React.ReactElement => {
        if (!submission) {
            return (<></>);
        }

        const rows = summaryRows.map((row, rowIndex) => {
            return (
                <div
                    className={'page-details-sidebar__item'}
                    key={`detail-row-${submission.id}-${rowIndex}`}
                >
                    <dt>{row.prompt}</dt>
                    <dd>
                        {row.tag && row.affirmative && !!row.value ?
                            (<span className={'cr-tag cr-tag--success'}>{row.value}</span>) :
                            row.value}
                    </dd>
                </div>
            );
        });

        // Valid HTML either way: the rows are <div> groups of <dt>/<dd>, which belong inside a <dl>.
        return inList ? (<React.Fragment>{rows}</React.Fragment>) : (
            <dl className={`page-details-sidebar__list${!!className ? ` ${className}` : ''}`}>
                {rows}
            </dl>
        );
    }

    return (
        <React.Fragment>
            {loadingUsers && loadingUsersContent()}
            {!loadingUsers && regularContent()}
        </React.Fragment>
    );
}

export const RequestSummary = connect<InjectedReduxState, InjectedActionCreators, RequestSummaryProps, ApplicationState>(
    (appState: ApplicationState, props: RequestSummaryProps) => {
        const commonText = GetPortalText<PortalTextCommon>(appState, 'commonText');
        const commonImportText = GetPortalText<PortalTextCommonImport>(appState, 'commonImport');

        let userDisplayName = props.loadedUserDisplayName;

        if (!userDisplayName) {
            if (!!props.requestSummary) {
                if (!!props.requestSummary.requestingUserId) {
                    const temp = GetUserById(appState, props.requestSummary.requestingUserId);
                    if (!!temp) {
                        userDisplayName = temp.displayName;
                    }
                }
            } else {
                userDisplayName = commonText?.tooltipAnonymousDC || '';
            }
        }

        const result = {
            loadingUsers: LoadingUsers(appState),
            userDisplayName,
            fileUploadDisabled: GetDisableAdditionalDocUpload(appState),
            provideUserCopyDisabled: GetDisableProvideuserCopy(appState),
            anonymizeResultsDisabled: GetDisableAnonymizeResults(appState),
            basisOfAccountingDisabled: GetDisableBasisOfAccounting(appState),
            defaultSubmissionOptions: GetDefaultSubmissionOptions(appState, 'shareableanonymous', false, commonImportText),
            commonText,
            srsText: GetPortalText<PortalTextSubmissionRequestSummary>(appState, 'submissionRequestSummary'),
        };

        return result;
    },
)(RequestSummaryComponent);

